import { useContext } from 'react'
import { AuthContext } from 'src/context/auth.context'
import { ApiResponse } from 'src/domain/api'
import { SalesReportWithTenant } from 'src/slices/services/api'
import AuthenticatedApi from 'src/slices/services/authenticatedApi'
import { axiosErrorMessage } from 'src/slices/services/error'
import getTotalCount from 'src/slices/services/handler'

const PER_PAGE = 300

export const useApiSalesReports = () => {
  const authContext = useContext(AuthContext)

  // 一部だけ返すと出力漏れのまま残りが「連携済み」にされるため、全件揃わなければ失敗にする
  const getAllByStore = async (
    orgCode: string,
    storeCode: string,
    startDate: string,
    endDate: string
  ): Promise<ApiResponse<SalesReportWithTenant[]>> => {
    const token = authContext.getAuthToken()
    if (!token) {
      return {
        success: false,
        message: 'ログインしなおしてください: token is not found',
      }
    }
    const api = new AuthenticatedApi(token)

    const fetchPage = (page: number) =>
      api.getOrganizationsOrganizationCodeStoresStoreCodeSalesReports(
        orgCode,
        storeCode,
        page,
        PER_PAGE,
        startDate,
        endDate
      )

    try {
      const firstPage = await fetchPage(1)
      const total = getTotalCount(firstPage)
      if (total === undefined || !Number.isFinite(total)) {
        return { success: false, message: '総件数を取得できませんでした' }
      }

      const lastPage = Math.ceil(total / PER_PAGE)
      const restPages = await Promise.all(
        Array.from({ length: Math.max(lastPage - 1, 0) }, (_, i) =>
          fetchPage(i + 2)
        )
      )
      if (restPages.some((res) => getTotalCount(res) !== total)) {
        return {
          success: false,
          message: '取得中に売上報告の件数が変わりました。再度お試しください',
        }
      }

      const reports = [firstPage, ...restPages].flatMap((res) => res.data)
      if (
        reports.length > total ||
        new Set(reports.map((report) => report.id)).size !== reports.length
      ) {
        return {
          success: false,
          message: '売上報告を全件取得できませんでした。再度お試しください',
        }
      }
      return { success: true, data: reports }
    } catch (e: unknown) {
      return { success: false, message: axiosErrorMessage(e) }
    }
  }

  return { getAllByStore }
}
